// =============================================================================
// supabase/functions/_shared/byod/prompt.ts
// -----------------------------------------------------------------------------
// The BYOD-lookup prompt, in ONE place so the Edge Function and the System Setup
// "AI Searches" editor agree. Pure (no imports / IO), so src can import it too
// (the editor seeds itself from DEFAULT_BYOD_PROMPT; the EF uses it as the
// fallback when the admin hasn't saved a custom one).
//
// TWO parts:
//   • DEFAULT_BYOD_PROMPT — the EDITABLE instruction text (admin-tunable, stored
//     in portal_config key "byod_lookup_prompt"). Placeholders {school},
//     {location}, {yearLevel} are filled per lookup.
//   • BYOD_JSON_SPEC — the FIXED output contract (the exact JSON keys), always
//     appended by buildByodPrompt(). It is NOT admin-editable because those keys
//     map 1:1 to the school_byod columns and the parser — changing them would break
//     saving. (So the tunable surface is the research guidance, not the schema.)
//
// BAND MODEL (db-198/db-199): BYOD policy varies by YEAR BAND, so a lookup returns ONE
// band (the one covering the child's year) plus its full year span as year_from/year_to
// on the Prep = 0 … Year 12 = 12 scale. year_levels is kept as a human-readable label.
// =============================================================================

export const BYOD_PROMPT_KEY = "byod_lookup_prompt";

export const DEFAULT_BYOD_PROMPT = [
  "You are researching the Bring-Your-Own-Device (BYOD) / student device policy for an Australian school.",
  "School: {school} ({location}).",
  "Use current, published sources — the school's own website / policy pages first.",
  "Determine, for the CURRENT school year, whether students bring their own device (BYOD) or the school supplies one.",
  "A school often runs DIFFERENT policies for different year bands (e.g. primary vs secondary, or a change at Year 7 or Year 9).",
  "The student is in year level {yearLevel}. Report the policy for the BAND THAT COVERS that year level, and state the FULL span of year levels that band applies to (its lowest and highest year).",
  "Express that span as year_from / year_to on this scale: Prep / Kindergarten = 0, Year 1..12 = 1..12 (a whole-school band = 0 to 12).",
  'If you are not sure, use null (or "unknown" for status) — do NOT guess.',
  "Never invent a policy URL — give one only if it is a real page you actually found.",
  "Do not include any personal data.",
].join("\n");

// Always appended after the (editable) instruction text — the fixed output shape.
export const BYOD_JSON_SPEC = [
  "Return ONLY a single JSON object (no prose, no markdown) with EXACTLY these keys:",
  '  "status": "byod" | "school_supplied" | "unknown"',
  '  "device": string | null            (e.g. "Windows laptop", "iPad (mandatory)")',
  '  "year_levels": string | null       (human-readable label for the band, e.g. "Years 7-12")',
  '  "year_from": integer | null        (band LOWEST year; Prep/Kindy = 0, Year N = N; whole-school = 0)',
  '  "year_to": integer | null          (band HIGHEST year on the same 0..12 scale; whole-school = 12)',
  '  "loan": true | false | null        (is a loan / hire device available?)',
  '  "loan_note": string | null',
  '  "hardship": true | false | null    (explicit hardship / financial-assistance provision?)',
  '  "policy_url": string | null        (the exact URL of the device/BYOD policy page, ONLY if you found it)',
  '  "notes": string | null',
  '  "confidence": "high" | "medium" | "low"',
].join("\n");

// BYOD Part 10 (10b.1) - JSON-FIRST. Grounding + a forced JSON schema can't be combined,
// so we ask for JSON inside the model's free text; ONE maxOutputTokens budget is shared
// across the model's thinking, any grounded prose, AND that JSON. On a hard case the JSON
// can lose the race and be truncated (finishReason MAX_TOKENS). Asking the model to emit
// the JSON object FIRST means a later cut-off loses only trailing prose, not the answer.
// Appended LAST by both builders so it is the final (strongest) instruction.
export const BYOD_JSON_FIRST_INSTRUCTION =
  "IMPORTANT: Output the JSON object as the VERY FIRST thing in your reply - before any reasoning, explanation, working, or citations. Emit the complete JSON object first; any prose after it is optional and may be omitted.";

// ── Annual policy-review date ────────────────────────────────────────────────
// Australian schools publish next year's BYOD policy around mid-December. This
// admin-configurable month/day is the rollover: a lookup done ON OR AFTER it is
// treated as being about NEXT year, and (Stage 5) a stored result that predates
// the most recent rollover is flagged stale. Stored in portal_config.
export const POLICY_REVIEW_KEY = "byod_policy_review_date";
export const DEFAULT_POLICY_REVIEW: { month: number; day: number } = { month: 12, day: 15 };

const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

// Coerce a stored value (or user input) to a valid { month, day }, clamped to a
// real calendar day. Falls back to the default on anything unusable.
export function normalizePolicyReview(value: unknown): { month: number; day: number } {
  const o = (value && typeof value === "object" ? value : {}) as { month?: unknown; day?: unknown };
  let month = Number(o.month);
  let day = Number(o.day);
  if (!Number.isInteger(month) || month < 1 || month > 12) month = DEFAULT_POLICY_REVIEW.month;
  const maxDay = DAYS_IN_MONTH[month - 1];
  if (!Number.isInteger(day) || day < 1 || day > maxDay) {
    day = month === DEFAULT_POLICY_REVIEW.month ? DEFAULT_POLICY_REVIEW.day : 1;
  }
  return { month, day };
}

// ── Thinking level ───────────────────────────────────────────────────────────
// gemini-3.5-flash reasoning effort: "low" is fast/cheap, "high" is most thorough.
// Admin-configurable (System Setup → AI Searches); default "medium" (the model
// default) — "low" answers were judged not worth having (John, 4 Sep 2026). Read
// by the EF's lookup generationConfig.thinkingConfig.thinkingLevel.
export const THINKING_LEVEL_KEY = "byod_thinking_level";
export type ThinkingLevel = "low" | "medium" | "high";
export const DEFAULT_THINKING_LEVEL: ThinkingLevel = "medium";

export function normalizeThinkingLevel(value: unknown): ThinkingLevel {
  const t = typeof value === "string" ? value.trim().toLowerCase() : "";
  return t === "low" || t === "medium" || t === "high" ? (t as ThinkingLevel) : DEFAULT_THINKING_LEVEL;
}

export interface ByodPromptVars {
  school: string;
  location: string; // "Suburb, STATE" (may be empty)
  yearLevel: string; // e.g. "Year 8" (may be empty)
}

// Fill the editable template's placeholders and append the fixed JSON spec.
// A blank/whitespace template falls back to DEFAULT_BYOD_PROMPT so a missing or
// cleared portal_config value can never send an empty instruction.
export function buildByodPrompt(template: string | null | undefined, vars: ByodPromptVars): string {
  const base = template && template.trim() ? template : DEFAULT_BYOD_PROMPT;
  const filled = base
    .split("{school}").join(vars.school || "")
    .split("{location}").join(vars.location || "")
    .split("{yearLevel}").join(vars.yearLevel || "not specified");
  return `${filled}\n\n${BYOD_JSON_SPEC}\n\n${BYOD_JSON_FIRST_INSTRUCTION}`;
}

// =============================================================================
// WHOLE-SCHOOL (multi-band) lookup — BYOD Part 7 / decision B2
// -----------------------------------------------------------------------------
// The admin "Edit School" BYOD manager has NO child and NO year, so it looks up
// EVERY band across Prep–12 in one call and writes several school_byod rows at
// once (the EF "save_bands" action). This is a SEPARATE prompt from the single-
// band responder lookup above (which stays one-band per decision #10) — it has no
// {yearLevel} placeholder and its JSON contract returns an ARRAY of bands.
// Admin-editable via its own portal_config key; blank falls back to the default.
// =============================================================================
// =============================================================================
// PHASE SCOPING - BYOD Part 10 (10a)
// -----------------------------------------------------------------------------
// A whole-school (Prep-12) multi-band lookup is the worst case for truncation - the
// model must find and segment policies across all 13 year levels. Scoping the search
// to ONE phase halves the range. The split is the standard QLD/AU boundary (Year 7 =
// high school). This is a SEARCH SCOPE ONLY - bands still store the real span the AI
// reports within the phase.
// =============================================================================
export const PHASE_PRIMARY: { yearFrom: number; yearTo: number } = { yearFrom: 0, yearTo: 6 };
export const PHASE_SECONDARY: { yearFrom: number; yearTo: number } = { yearFrom: 7, yearTo: 12 };

// The phase scope covering a child's numeric year level (Prep = 0 ... Year 12 = 12):
// year <= 6 -> Primary (0-6), year >= 7 -> Secondary (7-12). null when the year is
// unknown (the caller then falls back to a whole-school 0-12 search).
export function phaseScopeForYear(
  year: number | null | undefined,
): { yearFrom: number; yearTo: number } | null {
  if (year == null || typeof year !== "number" || !Number.isFinite(year)) return null;
  return year <= 6 ? { ...PHASE_PRIMARY } : { ...PHASE_SECONDARY };
}

// A human label for a phase scope - used in the scoped bands prompt (and safe for UI copy).
export function phaseLabelFor(yearFrom: number, yearTo: number): string {
  if (yearFrom <= 0 && yearTo >= 12) return "the whole school (Prep to Year 12)";
  if (yearTo <= 6) return "the primary school (Prep to Year 6)";
  if (yearFrom >= 7) return "the secondary school (Year 7 to Year 12)";
  return `years ${yearFrom} to ${yearTo}`;
}

export const BYOD_BANDS_PROMPT_KEY = "byod_bands_lookup_prompt";

export const DEFAULT_BYOD_BANDS_PROMPT = [
  "You are researching the Bring-Your-Own-Device (BYOD) / student device policy for an Australian school.",
  "School: {school} ({location}).",
  "Use current, published sources — the school's own website / policy pages first.",
  "Determine, for the CURRENT school year, the device policy across EVERY year level the school teaches, Prep to Year 12.",
  "A school often runs DIFFERENT policies for different year bands (e.g. primary vs secondary, or a change at Year 7 or Year 9). Identify EACH distinct band and report it separately.",
  "Return ONE entry per distinct band. The bands should together cover the year levels the school offers; do NOT overlap bands and do NOT invent year levels the school does not teach.",
  "Express each band's span as year_from / year_to on this scale: Prep / Kindergarten = 0, Year 1..12 = 1..12 (a single whole-school policy = one band from 0 to 12).",
  'If you are not sure about a band, use null (or "unknown" for status) — do NOT guess.',
  "Never invent a policy URL — give one only if it is a real page you actually found.",
  "Do not include any personal data.",
].join("\n");

// Always appended after the (editable) instruction text — the fixed ARRAY contract.
export const BYOD_BANDS_JSON_SPEC = [
  "Return ONLY a single JSON object (no prose, no markdown) with EXACTLY these keys:",
  '  "bands": an ARRAY of objects, each with EXACTLY these keys:',
  '      "status": "byod" | "school_supplied" | "unknown"',
  '      "device": string | null            (e.g. "Windows laptop", "iPad (mandatory)")',
  '      "year_levels": string | null       (human-readable label for the band, e.g. "Years 7-12")',
  '      "year_from": integer | null        (band LOWEST year; Prep/Kindy = 0, Year N = N; whole-school = 0)',
  '      "year_to": integer | null          (band HIGHEST year on the same 0..12 scale; whole-school = 12)',
  '      "loan": true | false | null        (is a loan / hire device available?)',
  '      "loan_note": string | null',
  '      "hardship": true | false | null    (explicit hardship / financial-assistance provision?)',
  '      "policy_url": string | null        (the exact URL of the device/BYOD policy page, ONLY if you found it)',
  '      "notes": string | null',
  '  "confidence": "high" | "medium" | "low"   (your overall confidence in the set of bands)',
].join("\n");

export interface ByodBandsPromptVars {
  school: string;
  location: string; // "Suburb, STATE" (may be empty)
  // BYOD Part 10 (10a) - optional PHASE scope (Primary 0-6 / Secondary 7-12). Omit, or
  // pass the full 0-12 range, for a whole-school search (the fallback).
  yearFrom?: number | null;
  yearTo?: number | null;
}

// Fill the whole-school template's placeholders and append the fixed ARRAY spec.
// Blank/whitespace falls back to DEFAULT_BYOD_BANDS_PROMPT. No {yearLevel} — the
// admin lookup is school-wide.
export function buildByodBandsPrompt(
  template: string | null | undefined,
  vars: ByodBandsPromptVars
): string {
  const base = template && template.trim() ? template : DEFAULT_BYOD_BANDS_PROMPT;
  const filled = base
    .split("{school}").join(vars.school || "")
    .split("{location}").join(vars.location || "");
  // BYOD Part 10 (10a) - when a phase scope is given (and it is NOT the whole 0-12 range),
  // constrain the search to that phase. Injected between the (editable) instructions and
  // the fixed ARRAY spec so admin-tuned guidance still applies.
  const from = typeof vars.yearFrom === "number" ? vars.yearFrom : null;
  const to = typeof vars.yearTo === "number" ? vars.yearTo : null;
  const scoped = from !== null && to !== null && !(from <= 0 && to >= 12);
  const scopeLine = scoped
    ? `\n\nSCOPE: Research and report ONLY the band(s) covering year levels ${from} to ${to} (${phaseLabelFor(from, to)}). Ignore year levels outside this range - do not report bands for them.`
    : "";
  return `${filled}${scopeLine}\n\n${BYOD_BANDS_JSON_SPEC}\n\n${BYOD_JSON_FIRST_INSTRUCTION}`;
}
